import { FC, useMemo, memo, useState, useRef } from 'react'
import { PdfPreviewPanel } from '../../../atoms/PdfPreviewPanel'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import config from '../../../../config'
import { dateFormatter } from '../../../../formatters/dateFormatter'
import _ from 'lodash'
import SplitPreviewLayout, { SplitPreviewLayoutHandles } from '../../../layouts/SplitPreviewLayout'
import { useCompanyPlanningRecord } from '../../../../hooks/companyPlanningRecords/useCompanyPlanningRecord'
import CircularProgress from '../../../atoms/CircularProgress'
import { store } from '../../../../redux/store'
import authSelectors from '../../../../redux/modules/auth/selectors'
import { stringFormatter } from '../../../../formatters/stringFormatter'
import { CompanyPlanningRecord } from '@leadgen/models/client'

type SentLetterOption = {
  label: string
  statusLabel: string
  url: string
  mailStatus:
    | CompanyPlanningRecord['submittedLetterStatus']
    | CompanyPlanningRecord['approvedLetterStatus']
  sentDate:
    | CompanyPlanningRecord['submittedLetterSentDate']
    | CompanyPlanningRecord['approvedLetterSentDate']
}

const getFileUrl = ({
  planningRecordId,
  fileName,
}: {
  planningRecordId: string
  fileName: string
}) => {
  const encodedUrlPart = encodeURIComponent(`planningRecordDocs/${planningRecordId}/${fileName}`)
  const url = `${config.bucketBaseUrl}${encodedUrlPart}?alt=media`
  return url
}

const getPlanningRecordIdFromCompanyPlanningRecordId = ({
  companyId,
  companyPlanningRecordId,
}: {
  companyId: string
  companyPlanningRecordId: string
}) => {
  const companyPrefix = `${companyId}_`
  return companyPlanningRecordId.startsWith(companyPrefix)
    ? companyPlanningRecordId.slice(companyPrefix.length)
    : companyPlanningRecordId
}

const getSentLetterOptions = (companyPlanningRecord: CompanyPlanningRecord) =>
  [
    {
      label: 'Submitted letter',
      statusLabel: 'Submitted',
      url: companyPlanningRecord.submittedLetterUrl,
      mailStatus: companyPlanningRecord.submittedLetterStatus,
      sentDate: companyPlanningRecord.submittedLetterSentDate,
    },
    {
      label: 'Approved letter',
      statusLabel: 'Approved',
      url: companyPlanningRecord.approvedLetterUrl,
      mailStatus: companyPlanningRecord.approvedLetterStatus,
      sentDate: companyPlanningRecord.approvedLetterSentDate,
    },
  ].filter(letterOption => Boolean(letterOption.url)) as SentLetterOption[]

const getDefaultSelectedFile = (companyPlanningRecord: CompanyPlanningRecord) =>
  _.orderBy(
    getSentLetterOptions(companyPlanningRecord),
    letterOption => (letterOption.sentDate ? new Date(letterOption.sentDate).getTime() : 0),
    'desc',
  )[0]?.url

const CompanyPlanningRecordDetails: FC<{
  onClose: () => void
  companyPlanningRecordId: string
}> = ({ companyPlanningRecordId, onClose }) => {
  const ref = useRef<SplitPreviewLayoutHandles>(null)
  const [selectedFile, setSelectedFile] = useState<string>()
  const { companyPlanningRecord, loading } = useCompanyPlanningRecord({
    companyPlanningRecordId,
    onLoadComplete: companyPlanningRecord => {
      setSelectedFile(getDefaultSelectedFile(companyPlanningRecord))
    },
  })
  const sentLetterOptions = useMemo(() => {
    return companyPlanningRecord ? getSentLetterOptions(companyPlanningRecord) : []
  }, [companyPlanningRecord])
  const selectedFileUrl = useMemo(() => {
    if (!selectedFile || !companyPlanningRecordId || !companyPlanningRecord) {
      return
    }
    if (sentLetterOptions.some(letterOption => letterOption.url === selectedFile)) {
      return selectedFile
    }
    const companyId = authSelectors.getCompanyId(store.getState())
    if (!companyId) {
      return
    }
    const planningRecordId = getPlanningRecordIdFromCompanyPlanningRecordId({
      companyId,
      companyPlanningRecordId,
    })
    return getFileUrl({
      planningRecordId,
      fileName: selectedFile,
    })
  }, [companyPlanningRecord, companyPlanningRecordId, selectedFile, sentLetterOptions])
  const applicantName = useMemo(() => {
    if (!companyPlanningRecord) {
      return '...'
    }
    const title =
      companyPlanningRecord.recipientType === 'agent'
        ? companyPlanningRecord.agentTitle
        : companyPlanningRecord.applicantTitle
    const firstName =
      companyPlanningRecord.recipientType === 'agent'
        ? companyPlanningRecord.agentFirstName
        : companyPlanningRecord.applicantFirstName
    const lastName =
      companyPlanningRecord.recipientType === 'agent'
        ? companyPlanningRecord.agentLastName
        : companyPlanningRecord.applicantLastName
    const companyName =
      companyPlanningRecord.recipientType === 'agent'
        ? companyPlanningRecord.agentCompanyName
        : companyPlanningRecord.applicantCompanyName
    if ((title && lastName) || (firstName && lastName)) {
      return [title, firstName, lastName]
        .filter((namePart): namePart is string => !!namePart)
        .map(namePart => _.startCase(namePart))
        .join(' ')
    } else {
      return companyName
    }
  }, [companyPlanningRecord])
  const categories = useMemo(() => {
    if (!companyPlanningRecord) {
      return '...'
    }
    return companyPlanningRecord.categories.map(category => _.startCase(category)).join(', ')
  }, [companyPlanningRecord])
  const classifications = useMemo(() => {
    if (!companyPlanningRecord) {
      return '...'
    }
    return companyPlanningRecord.classifications.join(', ')
  }, [companyPlanningRecord])
  return (
    <SplitPreviewLayout
      ref={ref}
      id="company-planning-record-details"
      onClose={onClose}
      preview={
        <>
          {companyPlanningRecord && (
            <PdfPreviewPanel
              url={selectedFileUrl}
              renderAnnotationLayer={false}
              renderTextLayer={false}
            />
          )}
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <CircularProgress delay={200} />
            </Box>
          )}
        </>
      }
      details={
        <>
          {companyPlanningRecord && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2">Description</Typography>
                <Typography variant={'body2'}>{companyPlanningRecord.description}</Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2">Address</Typography>
                <Typography variant={'body2'}>{companyPlanningRecord.address}</Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2">Recipient</Typography>
                <Typography variant={'body2'}>{applicantName}</Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2">Classifications</Typography>
                <Typography variant={'body2'}>{classifications}</Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2">Categories</Typography>
                <Typography variant={'body2'}>{categories}</Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="subtitle2">Application</Typography>
                <Box
                  component={'a'}
                  href={companyPlanningRecord.externalLink}
                  target="blank"
                  sx={{
                    ':focus': { outline: 'none' },
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                  }}
                >
                  {companyPlanningRecord.externalLink}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2">Processed on</Typography>
                {sentLetterOptions.map(letterOption => (
                  <Typography key={letterOption.label} variant={'body2'}>
                    {letterOption.statusLabel}:{' '}
                    {letterOption.sentDate
                      ? dateFormatter.formatDisplayDate(letterOption.sentDate)
                      : 'Unknown'}
                  </Typography>
                ))}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2">Status</Typography>
                {sentLetterOptions.map(letterOption => (
                  <Typography key={letterOption.label} variant={'body2'}>
                    {letterOption.statusLabel}:{' '}
                    {letterOption.mailStatus
                      ? stringFormatter.formatLetterStatus(letterOption.mailStatus)
                      : 'Unknown'}
                  </Typography>
                ))}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2">Files</Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  {sentLetterOptions.map(letterOption => (
                    <Chip
                      key={letterOption.label}
                      label={letterOption.label}
                      variant={selectedFile === letterOption.url ? 'filled' : 'outlined'}
                      onClick={() => {
                        setSelectedFile(letterOption.url)
                        ref.current?.setTabIndex(1)
                      }}
                    />
                  ))}
                  {companyPlanningRecord.applicationFile && (
                    <Chip
                      label="Application"
                      variant={
                        selectedFile === companyPlanningRecord.applicationFile
                          ? 'filled'
                          : 'outlined'
                      }
                      onClick={() => {
                        setSelectedFile(companyPlanningRecord.applicationFile as string)
                        ref.current?.setTabIndex(1)
                      }}
                    />
                  )}
                  {companyPlanningRecord.decisionFile && (
                    <Chip
                      label="Decision"
                      variant={
                        selectedFile === companyPlanningRecord.decisionFile ? 'filled' : 'outlined'
                      }
                      onClick={() => {
                        setSelectedFile(companyPlanningRecord.decisionFile as string)
                        ref.current?.setTabIndex(1)
                      }}
                    />
                  )}
                </Box>
              </Box>
            </>
          )}
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <CircularProgress delay={200} />
            </Box>
          )}
        </>
      }
    />
  )
}

export default memo(CompanyPlanningRecordDetails)
